class CartDrawerV2 {
  constructor(container) {
    this.container = container;

    this.single12VariantIds = (container.dataset.single12VariantIds || "")
      .split(",")
      .filter(Boolean)
      .map((id) => parseInt(id));

    this.init(container, true);

    // this only initialized once, globally, to open the cart drawer on cart toggle button click
    this.bindCartToggleEvents();
    this.updateCartContent();
  }

  bindCartToggleEvents() {
    const toggleButtons = document.querySelectorAll("[data-cart-toggle]");
    toggleButtons.forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        this.open();
      });
    });

    // added for debugging purposes
    document.addEventListener("keydown", (event) => {
      if (event.shiftKey && event.key === "C") {
        this.clearCart();
      }
    });
  }

  init(container, global = false) {
    this.overlay = container.querySelector(".cart-drawer-overlay");
    this.drawer = container.querySelector(".cart-drawer-container");
    this.closeButton = container.querySelector(".cart-drawer__close");
    this.continueButton = container.querySelector(".cart-drawer__continue");
    this.removeItemFromCartButtons = container.querySelectorAll("[data-remove-item-from-cart]");
    this.removeBundleFromCartButtons = container.querySelectorAll("[data-remove-bundle-from-cart]");
    this.variantSelectors = container.querySelectorAll("[data-variant-selector]");
    this.upgradeToSubscriptionButtons = container.querySelectorAll("[data-upgrade-to-subscription]");
    this.upsellAddToCartButtons = container.querySelectorAll("[data-upsell-add-to-cart]");
    this.upsellVariantSelectors = container.querySelectorAll("[data-upsell-variant-selector]");

    this.bindDrawerEvents();
    this.bindCartEvents(global ? document : container);

    // Bind shared drawer behaviors (escape to close, overlay/close button)
    if (typeof window !== "undefined" && window.DrawerBase) {
      window.DrawerBase.bind(this.container);
    }
  }

  bindDrawerEvents() {
    this.closeButton?.addEventListener("click", () => this.close());
    this.continueButton?.addEventListener("click", () => this.close());
    this.overlay?.addEventListener("click", () => this.close());

    this.removeItemFromCartButtons.forEach((button) => {
      button.addEventListener("click", this.#handleRemoveItemFromCart(button));
    });

    this.removeBundleFromCartButtons.forEach((button) => {
      button.addEventListener("click", this.#handleRemoveBundleFromCart(button));
    });

    this.variantSelectors.forEach((selector) => {
      selector.addEventListener("change", this.#handleVariantChange(selector));
    });

    this.upgradeToSubscriptionButtons.forEach((button) => {
      button.addEventListener("click", this.#handleUpgradeToSubscription(button));
    });

    this.upsellAddToCartButtons.forEach((button) => {
      button.addEventListener("click", this.#handleUpsellAddToCart(button));
    });

    this.upsellVariantSelectors.forEach((selector) => {
      selector.addEventListener("change", this.#handleUpsellVariantChange(selector));
    });
  }

  bindCartEvents(container) {
    const addButtons = container.querySelectorAll("[data-add-to-cart]");
    addButtons.forEach((button) => {
      button.addEventListener("click", this.#handleAddToCart(button));
    });
  }

  async removeBundleFromCart(bundleId) {
    try {
      this.container.classList.add("is-loading");
      const currentCart = await this.getCurrentCart();
      const bundleItems = currentCart.items.filter((item) => item.properties?._bundle_id === bundleId);
      const removeKeys = bundleItems.map((item) => item.key);

      const linkedItems = currentCart.items.filter(
        (item) =>
          item.properties?._shaker_bundle_id === bundleId || item.properties?._promo_gift_bundle_id === bundleId,
      );
      removeKeys.push(...linkedItems.map((item) => item.key));

      await this.simpleRemoveCartItems(removeKeys);
      await this.updateCartContent();
    } catch (error) {
      console.error("Error removing bundle from cart:", error);
      throw error;
    } finally {
      this.container.classList.remove("is-loading");
    }
  }

  async removeCartItems(itemKey) {
    try {
      this.container.classList.add("is-loading");
      await this.simpleRemoveCartItems(itemKey);
      await this.updateCartContent();
    } catch (error) {
      console.error("Error removing cart items:", error);
      throw error;
    } finally {
      this.container.classList.remove("is-loading");
    }
  }

  async upgradeBundleToSubscription(bundleId, sellingPlanId, targetBundleType, addOnVariantId = null) {
    try {
      this.container.classList.add("is-loading");
      const currentCart = await this.getCurrentCart();
      const bundleItems = currentCart.items.filter((item) => item.properties._bundle_id === bundleId);

      if (bundleItems.length === 0) {
        throw new Error("No bundle items found for upgrade");
      }

      const removeKeys = bundleItems.map((item) => item.key);

      const linkedItems = currentCart.items.filter(
        (item) =>
          item.properties?._shaker_bundle_id === bundleId || item.properties?._promo_gift_bundle_id === bundleId,
      );
      removeKeys.push(...linkedItems.map((item) => item.key));

      const transformation = {
        type: "upgrade_bundle_to_subscription",
        remove: removeKeys,
        add: {
          type: targetBundleType,
          variants: bundleItems.map((item) => item.variant_id),
          sellingPlan: sellingPlanId,
          addOn: addOnVariantId,
        },
      };

      await this.executeTransformation(transformation);
      await this.updateCartContent();
    } catch (error) {
      console.error("Error upgrading bundle to subscription:", error);
      throw error;
    } finally {
      this.container.classList.remove("is-loading");
    }
  }

  async changeItemVariant(itemKey, newVariantId) {
    try {
      this.container.classList.add("is-loading");
      const currentCart = await this.getCurrentCart();

      // Find the current item
      const currentItem = currentCart.items.find((item) => item.key === itemKey);
      if (!currentItem) {
        throw new Error("Item not found in cart");
      }

      const transformation = {
        type: "change_item_variant",
        remove: [itemKey],
        add: {
          variants: [newVariantId],
          sellingPlan: currentItem.selling_plan_allocation?.selling_plan?.id,
          properties: { ...currentItem.properties },
          quantity: currentItem.quantity || 1,
        },
      };

      await this.executeTransformation(transformation);
      await this.updateCartContent();
    } catch (error) {
      console.error("Error changing item variant:", error);
      throw error;
    } finally {
      this.container.classList.remove("is-loading");
    }
  }

  async addToCart(
    type,
    variants,
    sellingPlan,
    addOn,
    disableAutoBundle = false,
    promoGiftVariantId = null,
    promoKey = null,
  ) {
    this.container.classList.add("is-loading");

    try {
      const normalizedType = type === "1x12" ? "" : type;
      const shouldDisableAutoBundle = disableAutoBundle || type === "1x12";
      const variantIds = variants.split(",").map((v) => parseInt(v));
      const promoProperties = promoKey ? { _promo_key: promoKey } : null;
      await this.simpleAddToCart(
        variantIds,
        normalizedType,
        sellingPlan,
        promoProperties,
        addOn,
        promoGiftVariantId,
        promoKey,
      );
      if (!normalizedType && !shouldDisableAutoBundle) {
        await this.autoBundleSingle12s();
      }
      await this.updateCartContent();
    } catch (error) {
      throw error;
    } finally {
      this.container.classList.remove("is-loading");
    }
  }

  async getCurrentCart() {
    const response = await fetch("/cart.js");
    if (!response.ok) throw new Error("Failed to get cart");
    return await response.json();
  }

  // transformation object defines what transformations needs to be executed
  // to reach the desired state of the cart.
  //
  // Transformation object:
  // {
  //   type: "unsubscribe_bundle",
  //   remove: ["1234567890"],
  //   add: { variants: [1234567890], type: "60-day" },
  // }
  async executeTransformation(transformation) {
    try {
      if (transformation.remove) {
        await this.simpleRemoveCartItems(transformation.remove);
      }

      if (transformation.add) {
        await this.simpleAddToCart(
          transformation.add.variants,
          transformation.add.type,
          transformation.add.sellingPlan,
          transformation.add.properties,
          transformation.add.addOn,
          transformation.add.promoGift,
          transformation.add.promoKey,
          transformation.add.quantity,
        );
      }
    } catch (error) {
      console.error("Error executing transformation:", error);
      throw error;
    }
  }

  // removes items from cart by keys
  // keys can be a single key or an array of keys
  async simpleRemoveCartItems(keys) {
    if (!Array.isArray(keys)) {
      keys = [keys];
    }

    const updates = {};
    for (const key of keys) {
      updates[key] = 0;
    }

    const response = await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        updates,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to remove item from cart");
    }

    return await response.json();
  }

  // generates random 8 digit number to identify a bundle.
  generateIdentifier() {
    // generate a random 8 chars string
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    return result;
  }

  async simpleAddToCart(
    variants = [],
    type = null,
    sellingPlan = null,
    props = null,
    addOnVariantId = null,
    promoGiftVariantId = null,
    promoKey = null,
    quantity = 1,
  ) {
    if (!Array.isArray(variants)) {
      variants = [variants];
    }

    const items = [];
    let properties = props;
    let bundleId = null;

    if (type) {
      bundleId = this.generateIdentifier();
      properties = {
        ...properties,
        _bundle_type: type,
        _bundle_id: bundleId,
      };
    }

    for (const variantId of variants) {
      const key = this.generateIdentifier();
      items.push({
        id: variantId,
        quantity,
        selling_plan: sellingPlan ? parseInt(sellingPlan) : null,
        properties: {
          ...properties,
          _item_key: key,
        },
      });
    }

    let currentCart = null;
    if (addOnVariantId || promoGiftVariantId) {
      currentCart = await this.getCurrentCart();
    }

    if (addOnVariantId) {
      // Only allow one bottle per order
      const hasBottle = currentCart.items.some((item) => item.properties && item.properties._shaker_bundle_id);

      if (!hasBottle) {
        let addOnProperties = null;
        if (bundleId) {
          addOnProperties = {
            _shaker_bundle_id: bundleId,
          };
        }

        items.push({
          id: parseInt(addOnVariantId),
          quantity: 1,
          properties: addOnProperties,
        });
      }
    }

    if (promoGiftVariantId && promoKey) {
      const hasPromoGift = currentCart.items.some(
        (item) => item.properties?._promo_gift === "hydration_booster_12_pack",
      );

      if (!hasPromoGift) {
        items.push({
          id: parseInt(promoGiftVariantId),
          quantity: 1,
          properties: {
            _promo_gift: "hydration_booster_12_pack",
            _promo_gift_bundle_id: bundleId,
            _promo_key: promoKey,
          },
        });
      }
    }

    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: items,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to add item to cart");
    }

    return await response.json();
  }

  async clearCart() {
    const response = await fetch("/cart/clear.js", {
      method: "POST",
    });

    if (!response.ok) {
      throw new Error("Failed to clear cart");
    }

    return await response.json();
  }

  async updateCartContent() {
    try {
      // Get the current section ID
      const sectionId = this.getSectionId();

      if (!sectionId) {
        console.error("Cart drawer section ID not found");
        return;
      }

      // Fetch updated cart drawer HTML using Section Rendering API
      const response = await fetch(`${window.Shopify?.routes?.root || "/"}?section_id=${sectionId}`, {
        method: "GET",
        headers: {
          "Content-Type": "text/html",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const updatedHTML = await response.text();

      // Create a temporary container to parse the new HTML
      const tempContainer = document.createElement("div");
      tempContainer.innerHTML = updatedHTML;

      // Extract the cart drawer content from the updated HTML
      const newCartDrawer = tempContainer.querySelector("#cart-drawer-v2");

      if (newCartDrawer) {
        // Store current state
        const wasActive = this.container.classList.contains("is-active");

        // Replace the entire cart drawer content
        this.container.innerHTML = newCartDrawer.innerHTML;

        // Restore active state if it was active
        if (wasActive) {
          this.container.classList.add("is-active");
        }

        this.init(this.container);
      } else {
        console.error("Updated cart drawer content not found in response");
      }

      this.updateCartCount();
    } catch (error) {
      console.error("Error updating cart drawer:", error);
    }
  }

  async updateCartCount() {
    const currentCart = await this.getCurrentCart();

    this.cartCount = document.querySelectorAll("[data-cart-count]");
    this.cartCount.forEach((element) => {
      element.textContent = currentCart.item_count;
      if (currentCart.item_count === 0) {
        element.classList.add("hidden");
      } else {
        element.classList.remove("hidden");
      }
    });
  }

  getSectionId() {
    const sectionWrapper = this.container.closest(".shopify-section");
    if (sectionWrapper && sectionWrapper.id) {
      return sectionWrapper.id.replace("shopify-section-", "");
    }

    return "cart-drawer-v2";
  }

  #handleAddToCart(button) {
    return async (event) => {
      event.preventDefault();

      const redirectToCheckout =
        button.dataset.redirectToCheckout === "true" ||
        document.body.dataset.landingRedirectToCheckout === "true";
      if (!redirectToCheckout) {
        this.open();
      }

      if (button.dataset.clearCartBeforeAdd === "true") {
        await this.clearCart();
      }

      await this.addToCart(
        button.dataset.type,
        button.dataset.variants,
        button.dataset.sellingPlan,
        button.dataset.addOn,
        button.dataset.disableAutoBundle === "true",
        button.dataset.promoGift,
        button.dataset.promoKey,
      );

      if (redirectToCheckout) {
        this.redirectToCheckout();
      }
    };
  }

  redirectToCheckout() {
    window.location.assign("/checkout");
  }

  #handleRemoveBundleFromCart(button) {
    return (event) => {
      event.preventDefault();
      const bundleId = button.dataset.bundleId;
      this.removeBundleFromCart(bundleId);
    };
  }

  #handleRemoveItemFromCart(button) {
    return (event) => {
      event.preventDefault();
      const itemKey = button.dataset.itemKey;
      this.removeCartItems(itemKey);
    };
  }

  #handleUpsellAddToCart(button) {
    return async (event) => {
      event.preventDefault();
      const variantId = parseInt(button.dataset.variantId);
      await this.addUpsellToCart(variantId);
    };
  }

  #handleUpsellVariantChange(selector) {
    return (event) => {
      const row = selector.closest("[data-upsell-add-to-cart]")?.parentElement || selector.closest(".flex.gap-4");
      const addButton = row.querySelector("[data-upsell-add-to-cart]");
      const label = row.querySelector(".upsell-variant-label");

      if (addButton) {
        addButton.dataset.variantId = selector.value;
      }
      if (label) {
        label.textContent = selector.options[selector.selectedIndex].text;
      }
    };
  }

  async addUpsellToCart(variantId) {
    try {
      this.container.classList.add("is-loading");
      await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties: { _item_key: this.generateIdentifier() } }],
        }),
      });
      await this.autoBundleSingle12s();
      await this.updateCartContent();
    } catch (error) {
      console.error("Error adding upsell item to cart:", error);
    } finally {
      this.container.classList.remove("is-loading");
    }
  }

  async autoBundleSingle12s() {
    if (this.single12VariantIds.length === 0) return;

    const currentCart = await this.getCurrentCart();

    // Find unbundled items whose variant is a single 12-pack
    const unbundled12s = currentCart.items.filter(
      (item) =>
        !item.properties?._bundle_type &&
        !item.properties?._shaker_bundle_id &&
        !item.properties?._promo_gift &&
        this.single12VariantIds.includes(item.variant_id),
    );

    const existing2x12Items = currentCart.items.filter((item) => item.properties?._bundle_type === "2x12");

    if (existing2x12Items.length > 0 && unbundled12s.length >= 1) {
      const itemToAdd = unbundled12s[0];
      await this.#autoBundleTransformation([...existing2x12Items, itemToAdd], "upgrade_2x12_to_byo", "build-your-own");
      return;
    }

    if (unbundled12s.length >= 3) {
      await this.#autoBundleTransformation(unbundled12s.slice(0, 3), "auto_bundle_3x12", "build-your-own");
      return;
    }

    if (unbundled12s.length >= 2) {
      await this.#autoBundleTransformation(unbundled12s.slice(0, 2), "auto_bundle_2x12", "2x12");
      return;
    }
  }

  async #autoBundleTransformation(items, transformationType, bundleType) {
    await this.executeTransformation({
      type: transformationType,
      remove: items.map((item) => item.key),
      add: {
        type: bundleType,
        variants: items.map((item) => item.variant_id),
      },
    });
  }

  #handleUpgradeToSubscription(button) {
    return (event) => {
      event.preventDefault();
      const bundleId = button.dataset.bundleId;
      const sellingPlanId = parseInt(button.dataset.sellingPlanId);
      const targetBundleType = button.dataset.targetBundleType;
      this.upgradeBundleToSubscription(bundleId, sellingPlanId, targetBundleType, button.dataset.addOn);
    };
  }

  #handleVariantChange(selector) {
    return (event) => {
      event.preventDefault();
      const itemKey = selector.dataset.itemKey;
      const newVariantId = parseInt(selector.value);
      const currentVariantId = parseInt(selector.dataset.currentVariant);

      // Only proceed if the variant actually changed
      if (newVariantId !== currentVariantId) {
        this.changeItemVariant(itemKey, newVariantId);
      }
    };
  }

  open() {
    if (typeof window !== "undefined" && window.DrawerBase) {
      window.DrawerBase.open(this.container);
      return;
    }
    this.container.classList.add("is-active");
    document.body.style.overflow = "hidden";
  }

  close() {
    if (typeof window !== "undefined" && window.DrawerBase) {
      window.DrawerBase.close(this.container);
      return;
    }
    this.container.classList.remove("is-active");
    document.body.style.overflow = "";
  }
}

// Expose class for tests without affecting browser execution
if (typeof module !== "undefined" && module.exports) {
  module.exports = CartDrawerV2;
}
